import React from "react";

import classnames from "classnames";
import { type StyleProps, withStyleProps } from "Utils/styleProps";

import { Div } from "Common/HTML";

export interface BS3GridComponentProps extends StyleProps {
  children: React.ReactNode;
  className?: string;
  componentClass?: React.FunctionComponent<React.ComponentProps<typeof Div>>;
}

/**
 * Utility to check if Tailwind margin or padding classes are present
 * this allows us to dynamically add an opinionated default without conflicts
 * if the user specifies p={0}, m={0}, etc
 */
const hasTailwindSpacingClass = (className: string | undefined, type: "margin" | "padding"): boolean => {
  if (!className) return false;
  const regex = type === "margin" ? /\bm[xy]?-(-?\d+|auto|px)\b/ : /\bp[trblxy]?-(-?\d+|auto|px)\b/;
  return regex.test(className);
};

/**
 * 12 column grid layout
 * @note Formerly known as "Gridv3"
 * */
export const Grid = withStyleProps(({ children, componentClass: Component = Div, className = "", ...props }: BS3GridComponentProps) => (
  <Component className={classnames("mx-auto w-full max-w-screen-lg", className)} {...props}>
    {children}
  </Component>
));

/**
 * Row component - now powered by flexbox
 * @default mx is 4 (-16px) (Bootstrap 3 convention), please offset with column padding OR set to zero
 * @note Legacy Grid components have no notion of rows vs columns. Content will wrap if >12 columns
 * To override this behavior, pass in a className of "flex-row" or "flex-col"
 */
export const Row = withStyleProps(({ children, componentClass: Component = Div, className, m, mx, ...props }: BS3GridComponentProps) => {
  // Determine if margin overrides exist in className
  const hasMarginOverride = hasTailwindSpacingClass(className, "margin");
  // Apply default mx of -4 if neither m, mx, nor margin classes are present
  const marginX = m === undefined && mx === undefined && !hasMarginOverride ? -4 : mx;
  return (
    // Justify-start is to counteract an opinionated default in Grid.less w/ what should be the flexbox default
    <Component className={classnames("flex flex-wrap justify-start", className)} m={m} mx={marginX} {...props}>
      {children}
    </Component>
  );
});

export interface ColProps extends BS3GridComponentProps {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xsOffset?: number;
  smOffset?: number;
  mdOffset?: number;
  lgOffset?: number;
  xlOffset?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  smPush?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  mdPush?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  lgPush?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  xlPush?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  smPull?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  mdPull?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  lgPull?: number;
  /** @deprecated No longer supported, please use flexbox or flex order */
  xlPull?: number;
  // in practice most BS3 code we leveraged used offset, not push/pull
  // justify-between in the Row component accomplishes what those push/pulls were doing
  className?: string;
  children: React.ReactNode;
}

/**
 * Full-height column component
 * @default px is 4 (16px) - please zero out if needed
 * */
export const Col = withStyleProps(
  ({
    xs,
    sm,
    md,
    lg,
    xl,
    p,
    px,
    xsOffset,
    smOffset,
    mdOffset,
    lgOffset,
    xlOffset,
    className = "",
    componentClass: Component = Div,
    children,
    ...props
  }: ColProps) => {
    const classes = ["h-full"]; // full height by default for back compatibility

    // Default to full width if no size is specified
    if (!xs && !sm && !md && !lg && !xl) {
      classes.push("w-full");
    }

    // Map column sizes to Tailwind's width classes
    if (xs) {
      classes.push(`w-${xs}/12`);
    } else {
      // Default: full width at xs if not specified
      classes.push("w-full");
    }
    if (sm) {
      classes.push(`sm:w-${sm}/12`);
    }
    if (md) {
      classes.push(`md:w-${md}/12`);
    }
    if (lg) {
      classes.push(`lg:w-${lg}/12`);
    }
    if (xl) {
      classes.push(`xl:w-${xl}/12`);
    }

    // Map offset props to Tailwind's margin-left classes
    if (xsOffset) {
      classes.push(`ml-${xsOffset}/12`);
    }
    if (smOffset) {
      classes.push(`sm:ml-${smOffset}/12`);
    }
    if (mdOffset) {
      classes.push(`md:ml-${mdOffset}/12`);
    }
    if (lgOffset) {
      classes.push(`lg:ml-${lgOffset}/12`);
    }
    if (xlOffset) {
      classes.push(`xl:ml-${xlOffset}/12`);
    }

    // Determine if padding overrides exist in className
    const hasPaddingOverride = hasTailwindSpacingClass(className, "padding");

    /** Apply default px of 4 if neither p, px, nor padding classes are present */
    const paddingX = p === undefined && px === undefined && !hasPaddingOverride ? 4 : px;

    return (
      <Component className={classnames(classes, className)} p={p} px={paddingX} {...props}>
        {children}
      </Component>
    );
  },
);
